import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from 'antd';
import { appMessage as message } from '../utils/appMessage';
import { CloudDownloadOutlined, SearchOutlined, SyncOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { AppButton } from '../components/common/AppButton';
import { AppModal } from '../components/common/AppModal';
import { AppTag } from '../components/common/AppTag';
import { ExpansionFilterSelect } from '../components/common/ExpansionFilterSelect';
import { AppLoadError } from '../components/common/AppLoadError';
import { useExpansionCatalog } from '../hooks/UseExpansionCatalog';
import { useInlineLoadError } from '../hooks/UseAsyncData';
import { useStockPage } from '../contexts/StockPageContext';
import { useCardPage } from '../contexts/CardPageContext';
import { useStorage } from '../contexts/StorageContext';
import { useThemeTokens } from '../hooks/UseThemeTokens';
import { useIsCompact } from '../hooks/UseIsCompact';
import { downloadStockExcelTemplate, uploadStockExcel } from '../api/api';
import { saveDecidedPricesInBatches } from '../utils/decidedBatch';
import { currentSessionKey } from '../utils/decidedPrice';
import { decidedCardPatch } from '../utils/decidedCardPatch';
import { CARD_TYPE_FILTER_OPTIONS, Card, CardType, displaySellerCode } from '../types/card';
import type { StockExcelUploadResult } from '../types/stockExcel';
import {
  CONTROL_SIZE, FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, LAYOUT, RADIUS, SHADOW, SPACING, TABLE_CHECKBOX_SIZE, TOUCH_TARGET,
  type ThemeTokens,
} from '../constants/constants';
import { READ_ONLY_REASON } from '../constants/readOnly';

/**
 * 스토어 상품 관리 — claude.ai/design "스토어 상품 관리.dc.html" 포팅.
 *
 * 기존 가격 관리 / 재고 관리 탭 분리 화면을 한 표에서 판매가·재고를 같이
 * 편집하는 형태로 재구성한 안이다. 데이터·필터·잡은 기존 페이지와 같은 컨텍스트
 * (useStockPage / useCardPage)를 그대로 구독한다:
 *
 * - 표·판매상태 통계·레어도·확장팩·검색 → useStockPage()의 필터 파이프라인
 *   (재고 관리 탭과 필터 상태를 공유한다 — 두 화면을 오가며 비교하기 위함)
 * - 동기화(Naver→DB)          → useStockPage().startSync
 * - 가져오기(신규/재상장 등록)  → useCardPage().jobs.startImportProducts
 * - 재고조사 엑셀 다운로드/업로드 → downloadStockExcelTemplate / uploadStockExcel (StockToolbar와 동일)
 * - 인라인 판매가 수정 → 판매가 결정 API(PUT /{id}/decided-price)로 저장 · 네이버 반영 · PriceHub 보고를 한 번에(감사 #538 · #558 M4)
 * - 인라인 재고 수정  → 재고 카트(setCartItem, 증감량)로 담고 applyCart로 DB 저장 + Naver 반영
 *
 * 가격·재고를 한 표에서 함께 편집할 수 있는 스토어 상품 관리 화면이다.
 */

const won = (n: number) => n.toLocaleString('ko-KR');
const digits = (raw: string) => parseInt(raw.replace(/[^0-9]/g, ''), 10);

const TYPE_OPTIONS = CARD_TYPE_FILTER_OPTIONS;
const SIZE_OPTIONS = [50, 100, 200, 300, 500, 1000];
// 열 최소 폭 합 980px — 1280 화면에서 본문 폭(1,280 − 사이드바 220 − 좌우 패딩 48
// = 1,012px) 안에 들어와, 표 오른쪽 '가격 반영 · 재고 반영'이 내부 가로 스크롤 뒤에
// 숨지 않는다(2026-10-20 감사 "화면 · 표"). 예전엔 최소 1,104px 이라 마지막 열(92px)이
// 스크롤 뒤로 밀렸다. 상품명은 남는 폭을 받아 늘어난다.
const TABLE_COLS = '44px 48px minmax(200px,1fr) 120px 84px 108px 92px 92px 100px 92px';
// 1000~1279px 는 사이드바(220px)가 아직 남아 본문이 732~1,011px 로 좁아진다 — 기본 최소 폭
// 980px 로는 오른쪽 열이 스크롤 뒤에 남으므로, 이 구간에서만 열 폭을 줄여 1,000px 화면의
// 본문(732px, 브라우저 스크롤바까지 빼면 약 715px) 안에 들어오게 한다(합 708px).
// 조회가·판매가·재고 입력 칸은 값이 잘려도 입력 자체는 되도록 최소한만 남기고, 상품명은
// 남는 폭을 받는다(2026-10-20 PR #37 리뷰).
const TABLE_COLS_NARROW = '36px 32px minmax(120px,1fr) 80px 60px 80px 72px 72px 78px 78px';

/** 사이드바가 남는 좁은 구간(1000~1279px) — 표 열 폭을 줄일지 판단한다. */
const NARROW_TABLE_QUERY = '(max-width: 1279px)';

function useIsNarrowTable(): boolean {
  const [narrow, setNarrow] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(NARROW_TABLE_QUERY).matches
      : false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(NARROW_TABLE_QUERY);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    setNarrow(mql.matches);
    // 구형 Safari 는 MediaQueryList.addEventListener 가 없다 — addListener 로 폴백.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);
  return narrow;
}

// 표 체크박스 — 14×14 는 클릭 대상이 WCAG 2.5.8 최소(24×24)에 못 미쳤다. 레이아웃은
// 보이는 상자는 다른 표와 같은 18px(감사 card-controltower#437) — 누르는 곳은 상자를 감싼 label 이 칸 전체(줄 높이 54px).
const CHECKBOX_STYLE: React.CSSProperties = { width: TABLE_CHECKBOX_SIZE, height: TABLE_CHECKBOX_SIZE, margin: 0 };
const CHECKBOX_CELL: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'stretch', minHeight: TOUCH_TARGET, cursor: 'pointer',
};

// 레어도 3그룹 분류(디자인 시안의 기본 등급 / 하이레어·아트 / 특수 패턴) — 실제 rarity 문자열은
// 매장·게임마다 열려 있어 고정 목록으로 못 둔다. 한글이 섞이면 특수 패턴("미러" 등), 아래
// 기본 등급 집합에 있으면 기본, 나머지 알파벳 등급은 하이레어로 본다.
const BASIC_RARITIES = new Set(['C', 'U', 'UC', 'R', 'RR', 'RRR', 'P', 'D', 'S', 'SY', 'MA', 'N', 'TD', 'TR', 'PR']);
// 특수 패턴(로켓단 미러·마스터볼·이로치…)은 한글이 섞여 들어온다.
const isPatternRarity = (v: string) => /[가-힣]/.test(v);
function rarityGroupOf(v: string): 'basic' | 'high' | 'pattern' {
  if (isPatternRarity(v)) return 'pattern';
  return BASIC_RARITIES.has(v.toUpperCase()) ? 'basic' : 'high';
}
const RARITY_GROUP_TITLE: Record<'basic' | 'high' | 'pattern', string> = {
  basic: '기본 등급',
  high: '하이레어 · 아트',
  pattern: '특수 패턴',
};

/** 브라우저에 파일 다운로드를 트리거한다(StockToolbar와 동일). */
function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── 스타일 헬퍼 ────────────────────────────────────────────────────────────

const cardStyle = (t: ThemeTokens): React.CSSProperties => ({
  background: t.bgSurface,
  border: `1px solid ${t.border}`,
  borderRadius: RADIUS.lg,
});

const pageBtnStyle = (t: ThemeTokens, active: boolean, disabled: boolean): React.CSSProperties => ({
  minWidth: 28,
  height: 28,
  padding: `0 ${SPACING.xs}px`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: RADIUS.md,
  fontSize: FONT_SIZE.xs,
  fontVariantNumeric: 'tabular-nums',
  // 선택된 페이지 번호는 accentSolid 배경 + 흰 글자(라이트 5.17:1 · 다크 5.03:1).
  // 예전 accent 배경은 다크에서 흰 글자가 2.4:1 이라 번호가 거의 안 보였다(2026-10-20 감사).
  border: `1px solid ${active ? t.accentSolid : t.border}`,
  background: active ? t.accentSolid : t.bgSurface,
  color: active ? t.white : disabled ? t.textEmpty : t.textSecondary,
  fontWeight: active ? FONT_WEIGHT.bold : FONT_WEIGHT.regular,
  cursor: disabled ? 'not-allowed' : 'pointer',
  userSelect: 'none',
});

// ─── 상품 행 ────────────────────────────────────────────────────────────────

interface ProductRowProps {
  card: Card;
  t: ThemeTokens;
  gridTemplateColumns: string;
  checked: boolean;
  readOnly: boolean;
  priceEdited: boolean;
  stockEdited: boolean;
  priceValue: string;
  stockValue: string;
  onCheck: () => void;
  onPrice: (raw: string) => void;
  onStock: (raw: string) => void;
}

const ProductRow: React.FC<ProductRowProps> = ({
  card, t, gridTemplateColumns, checked, readOnly, priceEdited, stockEdited, priceValue, stockValue, onCheck, onPrice, onStock,
}) => {
  // PROHIBITED/PROHIBITION(네이버 판매금지)는 API 수정 자체가 불가 → 셀도 비활성 스타일.
  // VIEWER는 쓰기 권한이 없어 입력만 막고(disabled) 스타일은 평소대로 둔다.
  const locked = card.naverSaleStatus === 'PROHIBITED' || card.naverSaleStatus === 'PROHIBITION';
  const disabled = locked || readOnly;
  const targetStock = Number(stockValue);

  const editCell = (edited: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: CONTROL_SIZE.md.height,
    borderRadius: RADIUS.md,
    padding: `0 ${SPACING.xs}px`,
    border: `1px solid ${edited ? t.lineSuccess : 'transparent'}`,
    background: locked ? 'transparent' : edited ? t.tintSuccess : t.bgRaised,
    transition: 'background .12s, border-color .12s',
  });

  const inputStyle: React.CSSProperties = {
    textAlign: 'right',
    fontSize: FONT_SIZE.sm,
    fontWeight: locked ? FONT_WEIGHT.regular : FONT_WEIGHT.semibold,
    color: locked ? t.textMuted : t.textPrimary,
    padding: 0,
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns,
      alignItems: 'center',
      minHeight: 54,
      borderBottom: `1px solid ${t.borderSubtle}`,
      background: checked ? t.tintInfo : t.bgSurface,
    }}>
      {/* 조회 전용은 선택해도 할 일이 없다(일괄 수정 · 동기화는 관리자만) — 잠그고 이유를 보인다(유저 플로우 제안 7). */}
      <label style={CHECKBOX_CELL} title={readOnly ? READ_ONLY_REASON : undefined}>
        <input type="checkbox" checked={checked} onChange={onCheck} disabled={readOnly}
          aria-label={`${card.productName} 선택`}
          style={{ ...CHECKBOX_STYLE, accentColor: t.accent, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
      </label>

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {card.imageUrl
          ? <img src={card.imageUrl} alt="" style={{ width: 28, height: 39, objectFit: 'cover', borderRadius: RADIUS.sm, border: `1px solid ${t.border}` }} />
          : <div style={{ width: 28, height: 39, borderRadius: RADIUS.sm, background: t.bgPlaceholder, border: `1px solid ${t.border}` }} />}
      </div>

      <div style={{ padding: `0 ${SPACING.sm}px`, display: 'flex', alignItems: 'center', gap: SPACING.xs, minWidth: 0 }}>
        <AppTag variant="rarity" code={card.rarity} />
        <span style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {card.productName}
        </span>
      </div>

      <div style={{ padding: `0 ${SPACING.sm}px`, fontSize: FONT_SIZE.xs, color: t.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {displaySellerCode(card.sellerProductCode)
          || <span style={{ color: t.textMuted, fontStyle: 'italic' }}>코드 없음</span>}
      </div>

      <div style={{ padding: `0 ${SPACING.sm}px`, textAlign: 'right', fontSize: FONT_SIZE.xs }}>
        {card.suggestedPrice == null
          ? <span style={{ color: t.textMuted }}>미조회</span>
          : <span style={{ color: t.textSecondary }}>{won(card.suggestedPrice)}원</span>}
      </div>

      <div style={{ padding: `0 ${SPACING.xs}px` }}>
        <div style={editCell(priceEdited)} title={readOnly ? READ_ONLY_REASON : undefined}>
          <Input variant="borderless" size="small" value={priceValue} disabled={disabled}
            onChange={(e) => onPrice(e.target.value)} style={inputStyle} />
          <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, paddingLeft: SPACING.xxs }}>원</span>
        </div>
      </div>

      <div style={{ padding: `0 ${SPACING.xs}px` }}>
        <div style={editCell(stockEdited)} title={readOnly ? READ_ONLY_REASON : undefined}>
          <Input variant="borderless" size="small" value={stockValue} disabled={disabled}
            onChange={(e) => onStock(e.target.value)} style={inputStyle} />
        </div>
      </div>

      <div style={{ padding: `0 ${SPACING.xs}px`, display: 'flex', justifyContent: 'center' }}>
        {card.naverSaleStatus
          ? <AppTag variant="sale" status={card.naverSaleStatus} />
          : <span style={{ color: t.textEmpty, fontSize: FONT_SIZE.xs }}>-</span>}
      </div>

      <div style={{ padding: `0 ${SPACING.xs}px`, textAlign: 'center', fontSize: FONT_SIZE.xs }}>
        {priceEdited
          ? <span style={{ color: t.warning, fontWeight: FONT_WEIGHT.semibold }}>대기</span>
          : card.naverSyncedAt
            ? <span style={{ color: t.success }}>{dayjs(card.naverSyncedAt).format('MM/DD HH:mm')}</span>
            : <span style={{ color: t.textEmpty }}>-</span>}
      </div>

      <div style={{ padding: `0 ${SPACING.xs}px`, textAlign: 'center', fontSize: FONT_SIZE.xs }}>
        {stockEdited
          ? <span style={{ color: t.warning, fontWeight: FONT_WEIGHT.semibold }}>대기</span>
          : card.stockSyncedAt
            ? <span style={{ color: t.success }}>{dayjs(card.stockSyncedAt).format('MM/DD HH:mm')}</span>
            : targetStock === 0
              ? <span style={{ color: t.textSecondary }}>품절</span>
              : <span style={{ color: t.textEmpty }}>-</span>}
      </div>
    </div>
  );
};

// ─── 페이지 ─────────────────────────────────────────────────────────────────

export const StoreProductManagementPage: React.FC = () => {
  const t = useThemeTokens();
  const { isAdmin, store, superStore } = useStorage();
  // 좁은 폭에서는 사이드바가 Drawer 로 접히므로 하단 저장 바가 화면 왼쪽 끝에서 시작한다.
  const compact = useIsCompact();
  // 1000~1279px 는 사이드바가 남는 좁은 구간 — 이때만 표 열 최소 폭을 줄인다.
  const narrowTable = useIsNarrowTable();
  const tableCols = narrowTable ? TABLE_COLS_NARROW : TABLE_COLS;

  const {
    cards, loading, error, retry, hasLoadedOnce, setInlineError,
    search, setSearch,
    typeFilter, setTypeFilter,
    expansionFilter, setExpansionFilter, expansionOptions,
    rarityFilter, setRarityFilter, rarityOptions,
    saleStatusFilter, setSaleStatusFilter, saleStatusOptions, saleStatusCounts,
    filtered,
    cart, addToCart, clearCart, setCartItem, resolveTargetStock,
    syncing, startSync, applyCart, applying,
  } = useStockPage();

  const { cards: cardData, jobs } = useCardPage();

  // 이 화면은 재조회 실패를 인라인 배너(AppLoadError inline)로 직접 그린다 — 마운트 동안만
  // 토스트를 끈다. 카드/재고 훅은 여러 화면이 공유하므로, 벗어나면 다시 켜져 배너를 그리지
  // 않는 화면(상품 등록·반영 로그 등)은 예전처럼 토스트로 알린다(2026-10-21 회귀 수정).
  useInlineLoadError(setInlineError);

  // ── 로컬 상태 ────────────────────────────────────────────────────────────
  const [priceEdits, setPriceEdits] = useState<Map<number, number>>(new Map());
  // 편집은 카드 id 로만 들고 있어 매장이 바뀌면 새 매장의 같은 id 카드 위에 보일 수 있었다(시우 검수 #96, 감사 card-controltower#839) —
  // 편집이 어느 매장 것인지 같이 두고, 매장이 바뀐 렌더에서 바로 비운다(재고 카트의 매장별 키와 같은 기준 — superStore → store).
  const editScope = superStore || store || 'none';
  const [priceEditScope, setPriceEditScope] = useState(editScope);
  if (priceEditScope !== editScope) {
    setPriceEditScope(editScope);
    setPriceEdits(new Map());
  }
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [size, setSize] = useState(100);
  const [page, setPage] = useState(1);
  const [jump, setJump] = useState('1');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<StockExcelUploadResult | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ── 검색 디바운스 (StockToolbar와 동일 패턴) ──────────────────────────────
  const [searchDraft, setSearchDraft] = useState(search);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((v: string) => {
    setSearchDraft(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(v), 300);
  }, [setSearch]);
  useEffect(() => { setSearchDraft(search); }, [search]);

  const byId = useMemo(() => new Map(cards.map((c) => [c.id, c])), [cards]);
  const expansionCatalog = useExpansionCatalog(typeFilter, expansionOptions);
  const expansionPicked = typeFilter !== 'ALL' && expansionFilter !== 'ALL';

  // ── 레어도 3그룹 (현재 종류 기준 건수 포함) ─────────────────────────────
  const rarityGroups = useMemo(() => {
    const inType = typeFilter === 'ALL' ? cards : cards.filter((c) => c.cardType === typeFilter);
    const count = new Map<string, number>();
    for (const c of inType) if (c.rarity) count.set(c.rarity, (count.get(c.rarity) ?? 0) + 1);

    const groups: Record<'basic' | 'high' | 'pattern', { code: string; count: number }[]> =
      { basic: [], high: [], pattern: [] };
    for (const opt of rarityOptions) {
      groups[rarityGroupOf(opt.value)].push({ code: opt.value, count: count.get(opt.value) ?? 0 });
    }
    return (['basic', 'high', 'pattern'] as const)
      .map((key) => ({ key, title: RARITY_GROUP_TITLE[key], chips: groups[key] }))
      .filter((g) => g.chips.length > 0);
  }, [cards, typeFilter, rarityOptions]);

  const toggleRarity = (code: string) =>
    setRarityFilter(rarityFilter.includes(code)
      ? rarityFilter.filter((c) => c !== code)
      : [...rarityFilter, code]);

  // ── 편집 ────────────────────────────────────────────────────────────────
  const setPrice = (id: number, raw: string) => {
    const card = byId.get(id);
    if (!card) return;
    const base = card.currentPrice ?? 0;
    const n = digits(raw);
    const v = Number.isNaN(n) ? 0 : n;
    setPriceEdits((cur) => {
      const next = new Map(cur);
      if (v === base) next.delete(id); else next.set(id, v);
      return next;
    });
  };

  const setStock = (id: number, raw: string) => {
    const card = byId.get(id);
    if (!card) return;
    const n = digits(raw);
    const v = Number.isNaN(n) ? 0 : n;
    setCartItem(id, { value: v - (card.stockQuantity ?? 0) });
  };

  const cartDirtyIds = useMemo(
    () => Array.from(cart.entries()).filter(([, it]) => Math.floor(it.value) !== 0).map(([id]) => id),
    [cart],
  );
  const dirtyCount = priceEdits.size + cartDirtyIds.length;

  // ── 페이지네이션 ────────────────────────────────────────────────────────
  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const curPage = Math.min(page, pageCount);
  const from = total === 0 ? 0 : (curPage - 1) * size + 1;
  const to = Math.min(curPage * size, total);
  const rows = filtered.slice((curPage - 1) * size, curPage * size);
  const heavyWarning = size >= 500;

  // 첫 조회부터 실패해 보여 줄 목록이 없는 상태. 예전엔 `rows.length === 0` 으로 판단해서
  // ① 진짜 0건이 정상 응답인데 재조회가 실패한 경우와 ② 필터/검색 결과가 0건(또는 현재
  // 페이지가 빈 경우)인데 재조회가 실패한 경우까지 표를 전체 오류 화면으로 덮었다 —
  // "성공한 조회가 있었는지"로 판단한다(BuybackPage·DailyWorkflowPage 와 같은 기준,
  // 2026-10-21 리뷰). 성공한 적이 있으면 표를 유지하고 위에 인라인 배너만 붙인다.
  const failedEmpty = !!error && !hasLoadedOnce;

  const goPage = (n: number) => {
    const p = Math.min(Math.max(1, n), pageCount);
    setPage(p);
    setJump(String(p));
  };

  const pageNums = useMemo(() => {
    const nums: number[] = [];
    const push = (n: number) => { if (!nums.includes(n) && n >= 1 && n <= pageCount) nums.push(n); };
    push(1); push(2);
    for (let n = curPage - 1; n <= curPage + 1; n++) push(n);
    push(pageCount - 1); push(pageCount);
    return nums.sort((a, b) => a - b);
  }, [curPage, pageCount]);

  // ── 선택 ────────────────────────────────────────────────────────────────
  const allChecked = rows.length > 0 && rows.every((r) => sel.has(r.id));
  const toggleAll = () => setSel((cur) => {
    const next = new Set(cur);
    if (rows.every((r) => next.has(r.id))) rows.forEach((r) => next.delete(r.id));
    else rows.forEach((r) => next.add(r.id));
    return next;
  });
  const toggleRow = (id: number) => setSel((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selIds = useMemo(() => Array.from(sel), [sel]);

  // ── 액션 ────────────────────────────────────────────────────────────────
  const bulkStockZero = () => {
    if (!selIds.length) return;
    selIds.forEach((id) => {
      const c = byId.get(id);
      if (!c || c.naverSaleStatus === 'PROHIBITED' || c.naverSaleStatus === 'PROHIBITION') return;
      setCartItem(id, { value: -(c.stockQuantity ?? 0) });
    });
  };

  const handleDownloadExcel = async () => {
    if (!expansionPicked) return;
    setDownloading(true);
    try {
      const blob = await downloadStockExcelTemplate(typeFilter as CardType, expansionFilter);
      triggerBrowserDownload(blob, `재고조사_${expansionFilter}_${dayjs().format('YYYYMMDD')}.xlsx`);
    } catch {
      message.error('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || typeFilter === 'ALL') return;
    setUploading(true);
    try {
      setUploadResult(await uploadStockExcel(typeFilter as CardType, file));
    } catch {
      message.error('엑셀 업로드 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  // 업로드 결과를 재고 카트에 담는다(절대값 → 증감량 변환, StockToolbar와 동일). DB에는
  // 아직 안 쓴다 — 표에 대기 상태로 뜨고, 사용자가 "변경사항 저장 · 반영"을 눌러야 나간다.
  const applyUpload = () => {
    if (!uploadResult || uploadResult.matched.length === 0) return;
    addToCart(uploadResult.matched.map((m) => m.id));
    uploadResult.matched.forEach((m) => setCartItem(m.id, { value: m.uploadedQuantity - m.currentStock }));
    setUploadOpen(false);
    message.success(`엑셀 매칭 ${uploadResult.matched.length}건을 재고 수정 대기에 담았습니다.`);
  };

  const revert = () => {
    setPriceEdits(new Map());
    clearCart();
  };

  // 판매가 저장(감사 card-controltower#538 · #558 M4) — 판매가 결정 API 로 저장 · 네이버 반영 · PriceHub 보고가 한 번에. 여러 장은 백엔드 PR #97
  // 의 batch 로 20장씩 차례로(감사 #649 · #819 — 단건을 여러 번 보내면 매장 가격 쓰기 락이 겹쳐 409 가 다수 났다), 409 는 서버가 준 시간 뒤
  // 같은 요청 번호로 다시, 옛 서버면 단건으로(utils/decidedBatch). 진행을 N/전체로 알리고, 끝나면 목록을 다시 받는다.
  const savePrices = async (entries: [number, number][]) => {
    const byId = new Map(cardData.cards.map((c) => [c.id, c]));
    const patched: Card[] = [];
    const done: number[] = [];
    let naverOk = 0;
    let naverFail = 0;
    let naverUnknown = 0;
    let mismatch = 0;
    const rejected: string[] = [];
    const queue = entries.filter(([, p]) => p > 0);
    const zero = entries.length - queue.length;
    const outcomes = await saveDecidedPricesInBatches(
      queue.map(([id, price]) => ({ cardId: id, price, sellerProductCode: byId.get(id)?.sellerProductCode ?? null })),
      {
        onProgress: ({ done: n, total, failed }) => {
          if (total <= 1) return;
          message.loading({
            key: 'store-save',
            content: `판매가 저장 중 ${n.toLocaleString()}/${total.toLocaleString()}${failed > 0 ? ` · 저장 못 함 ${failed.toLocaleString()}` : ''}`,
            duration: 0,
          });
        },
      },
    );
    outcomes.forEach((o, id) => {
      if (o.kind === 'saved') {
        const r = o.result;
        const card = byId.get(id);
        if (card) patched.push(decidedCardPatch(card, r.price, r.naver.status === 'SUCCESS'));
        done.push(id);
        if (r.naver.status === 'SUCCESS') naverOk += 1;
        else if (r.naver.status === 'UNKNOWN') naverUnknown += 1;
        else naverFail += 1;
        return;
      }
      if (o.kind === 'mismatch') mismatch += 1;
      rejected.push(`${byId.get(id)?.productName ?? `#${id}`}: ${o.message}`);
    });
    if (patched.length > 0) cardData.mergeResults(patched);
    // 저장된 줄만 편집 표시를 지운다 — 거절된 줄은 남겨 다시 저장할 수 있게.
    setPriceEdits((prev) => {
      const next = new Map(prev);
      done.forEach((id) => next.delete(id));
      return next;
    });
    // 서버 값으로 목록을 다시 받는다(네이버 결과 · 보호 표시 · 다른 사람이 바꾼 값). 판매자 상품코드 불일치면 특히 필요하다.
    // 재조회는 끝날 때까지 기다린다 — 가격 · 재고를 함께 저장할 때 재고 반영(applyCart)이 먼저 시작되면 늦게 온 이 조회 응답이 재고 결과를
    // 덮었다(감사 card-controltower#857). 조회 실패는 화면의 인라인 배너가 알린다.
    if (outcomes.size > 0) {
      try { await cardData.reload(); } catch { /* 배너가 알린다 */ }
    }
    const parts = [`판매가 ${done.length}건 저장`];
    if (done.length > 0) parts.push(`네이버 반영 ${naverOk}건`);
    if (naverFail > 0) parts.push(`네이버 반영 실패 ${naverFail}건(③ 에서 다시)`);
    if (naverUnknown > 0) parts.push(`결과 불명 ${naverUnknown}건(반영 로그 확인)`);
    if (zero > 0) parts.push(`0원 ${zero}건 건너뜀`);
    if (mismatch > 0) parts.push(`목록이 바뀐 카드 ${mismatch}건 — 목록을 새로 받은 뒤 다시 저장하세요`);
    if (rejected.length > 0) parts.push(`저장 못 함 ${rejected.length}건 — ${rejected.slice(0, 3).join(' / ')}${rejected.length > 3 ? ' …' : ''}`);
    return { text: parts.join(' · '), clean: rejected.length === 0 && naverFail === 0 && naverUnknown === 0 && zero === 0 };
  };
  // 화면이 붙어 있는가 — SUPER 매장 전환은 카드 화면을 다시 마운트한다. 재조회를 기다리던 앞 화면의 저장 함수가 새 매장 헤더로 재고 반영을
  // 보내지 않게(감사 card-controltower#872).
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const save = async () => {
    const priceEntries = Array.from(priceEdits.entries());
    if (priceEntries.length === 0 && cartDirtyIds.length === 0) return;
    // 시작한 세션(계정 · 매장 · 로그인 시각) — 재조회를 기다리는 사이 바뀌면 재고 반영을 시작하지 않는다(감사 #872).
    const session = currentSessionKey();
    setSaving(true);
    try {
      const notes: string[] = [];
      let clean = true;
      if (priceEntries.length > 0) {
        const r = await savePrices(priceEntries);
        notes.push(r.text);
        clean = clean && r.clean;
      }
      if (cartDirtyIds.length > 0) {
        if (!mountedRef.current || currentSessionKey() !== session) {
          message.warning({
            key: 'store-save',
            content: [...notes, '매장 · 로그인이 바뀌어 재고 반영은 시작하지 않았습니다 — 목록을 다시 확인한 뒤 저장하세요'].join(' · '),
            duration: 10,
          });
          return;
        }
        // updateStockBulk(동기) → applyStock(잡). 완료 시 카트 비움. 시작 못 했으면 그렇게 알린다(#535).
        const started = await applyCart();
        notes.push(started ? '재고 반영 작업을 시작했습니다' : '재고 반영은 시작하지 못했습니다');
        clean = clean && started;
      }
      (clean ? message.success : message.warning)({ key: 'store-save', content: notes.join(' · '), duration: clean ? 4 : 10 });
    } catch {
      message.error('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const raritySummary = rarityFilter.length ? `${rarityFilter.length}개 선택됨` : '전체 레어도';
  const excelDisabledReason = expansionPicked ? undefined : '카드 종류와 확장팩을 먼저 선택하세요';

  const statTabs: { key: string; label: string; count: number }[] = [
    { key: 'ALL', label: '전체', count: cards.length },
    ...saleStatusOptions.map((o) => ({ key: o.value, label: o.label, count: saleStatusCounts[o.value] ?? 0 })),
  ];

  return (
    <DashboardLayout title="스토어 상품 관리">
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm, paddingBottom: compact ? SPACING['3xl'] * 3 : SPACING['3xl'] * 2 }}>

        {/* ── 판매상태 통계 + 동기화 / 가져오기 ── */}
        <div style={{
          ...cardStyle(t), display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: SPACING.md, padding: `${SPACING.xs}px ${SPACING.sm}px`,
          // 390px 에서 탭 줄과 버튼이 서로 밀어내 한쪽이 잘렸다 — 좁은 폭에서는 탭 줄을
          // 가로 스크롤시키고, 버튼은 필요하면 다음 줄로 내린다(2026-10-20 코덱 3차 지적).
          flexWrap: compact ? 'wrap' : 'nowrap',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            flexWrap: compact ? 'nowrap' : 'wrap',
            overflowX: compact ? 'auto' : 'visible',
            minWidth: 0, flex: '1 1 auto',
          }}>
            {statTabs.map((tab) => {
              const on = saleStatusFilter === tab.key || (tab.key === 'ALL' && saleStatusFilter === 'ALL');
              return (
                <div
                  key={tab.key}
                  onClick={() => setSaleStatusFilter(tab.key === 'ALL' ? 'ALL' : (saleStatusFilter === tab.key ? 'ALL' : tab.key))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: SPACING.xxs,
                    padding: `${SPACING.xs}px ${SPACING.sm}px`, borderRadius: RADIUS.md,
                    cursor: 'pointer', userSelect: 'none',
                    flexShrink: 0, whiteSpace: 'nowrap',
                    background: on ? t.tintInfo : 'transparent',
                    color: on ? t.accent : tab.count === 0 ? t.textEmpty : t.textSecondary,
                    fontWeight: on ? FONT_WEIGHT.bold : FONT_WEIGHT.regular,
                  }}
                >
                  <span style={{ fontSize: FONT_SIZE.sm }}>{tab.label}</span>
                  <span style={{
                    fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.bold, fontVariantNumeric: 'tabular-nums',
                    color: on ? t.accent : tab.count === 0 ? t.textEmpty : t.textPrimary,
                  }}>
                    {won(tab.count)}
                  </span>
                </div>
              );
            })}
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, flex: '0 0 auto' }}>
              <AppButton
                variant="default" size={compact ? 'lg' : 'md'} icon={<SyncOutlined />} loading={syncing}
                onClick={() => startSync(selIds.length ? selIds : [])}
              >
                동기화 ({won(selIds.length || total)})
              </AppButton>
              <AppButton
                variant="default" size={compact ? 'lg' : 'md'} icon={<CloudDownloadOutlined />} loading={jobs.importing}
                onClick={() => jobs.startImportProducts()}
              >
                가져오기
              </AppButton>
            </div>
          )}
        </div>

        {/* ── 확장팩 · 검색 · 레어도 필터 ── */}
        <div style={{ ...cardStyle(t), padding: compact ? SPACING.sm : SPACING.md, display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: SPACING.xs,
            // 390px: 종류(선택) + 확장팩(240px 고정) + 검색(최소 220px)이 한 줄에 못 들어가
            // 오른쪽이 잘렸다 — 좁은 폭에서는 컨트롤을 세로로 쌓는다(2026-10-20 코덱 3차 지적).
            flexDirection: compact ? 'column' : 'row',
            flexWrap: compact ? 'nowrap' : 'wrap',
          }}>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as CardType | 'ALL')}
              style={{
                height: 32, padding: `0 ${SPACING.xs}px`, border: `1px solid ${t.border}`,
                borderRadius: RADIUS.md, background: t.bgSurface, fontSize: FONT_SIZE.sm, color: t.textPrimary,
                width: compact ? '100%' : undefined,
              }}
            >
              {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ExpansionFilterSelect
              value={expansionFilter}
              onChange={setExpansionFilter}
              options={expansionCatalog.options}
              loading={expansionCatalog.loading}
              gameType={typeFilter}
              style={{ width: compact ? '100%' : 240 }}
            />
            <Input
              value={searchDraft}
              onChange={(e) => handleSearchChange(e.target.value)}
              prefix={<SearchOutlined style={{ color: t.textMuted }} />}
              placeholder="상품명 검색"
              allowClear
              style={{
                flex: compact ? '0 0 auto' : 1,
                minWidth: compact ? 0 : 220,
                width: compact ? '100%' : undefined,
              }}
            />
            <div style={{
              fontSize: FONT_SIZE.xs, color: t.textMuted, paddingLeft: SPACING.xxs,
              alignSelf: compact ? 'flex-end' : undefined,
            }}>
              총 <span style={{ color: t.textPrimary, fontWeight: FONT_WEIGHT.semibold }}>{won(total)}</span>건
            </div>
          </div>

          {rarityGroups.length > 0 && (
            <div style={{ border: `1px solid ${t.borderSubtle}`, borderRadius: RADIUS.lg, background: t.bgRaised }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACING.xs,
                flexWrap: 'wrap',
                padding: `${SPACING.sm}px ${SPACING.md}px`, borderBottom: `1px solid ${t.borderSubtle}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACING.xs }}>
                  <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>레어도 필터</span>
                  <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>선택 안 하면 전체</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
                  <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>{raritySummary}</span>
                  <div
                    onClick={() => setRarityFilter([])}
                    style={{
                      fontSize: FONT_SIZE.xs, height: 26, padding: `0 ${SPACING.xs}px`, display: 'flex',
                      alignItems: 'center', border: `1px solid ${t.border}`, borderRadius: RADIUS.md,
                      background: t.bgSurface, color: t.textSecondary, cursor: 'pointer', userSelect: 'none',
                    }}
                  >
                    선택 해제
                  </div>
                </div>
              </div>

              {rarityGroups.map((group, gi) => {
                const codes = group.chips.map((c) => c.code);
                const allOn = codes.every((c) => rarityFilter.includes(c));
                return (
                  <div key={group.key} style={{
                    display: 'flex', gap: SPACING.md, padding: `${SPACING.sm}px ${SPACING.md}px`,
                    borderTop: gi === 0 ? 'none' : `1px solid ${t.borderSubtle}`,
                  }}>
                    <div style={{ flex: '0 0 110px', display: 'flex', flexDirection: 'column', gap: SPACING.xxs, paddingTop: SPACING.xxs }}>
                      <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>{group.title}</span>
                      <span
                        onClick={() => setRarityFilter(allOn
                          ? rarityFilter.filter((c) => !codes.includes(c))
                          : Array.from(new Set([...rarityFilter, ...codes])))}
                        style={{ fontSize: FONT_SIZE.xs, color: t.accent, cursor: 'pointer', userSelect: 'none' }}
                      >
                        {allOn ? '모두 해제' : '모두 선택'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING.xxs, flex: 1 }}>
                      {group.chips.map(({ code, count }) => {
                        const on = rarityFilter.includes(code);
                        return (
                          <div key={code} onClick={() => toggleRarity(code)} style={{
                            display: 'inline-flex', alignItems: 'center', gap: SPACING.xxs, height: 26,
                            padding: `0 ${SPACING.xs}px`, borderRadius: RADIUS.md, fontSize: FONT_SIZE.xs,
                            lineHeight: LINE_HEIGHT.none, cursor: 'pointer', userSelect: 'none',
                            background: on ? t.tintInfo : t.bgSurface,
                            border: `1px solid ${on ? t.lineAccent : t.border}`,
                            color: on ? t.accent : t.textSecondary,
                            fontWeight: on ? FONT_WEIGHT.bold : FONT_WEIGHT.regular,
                          }}>
                            <span>{code}</span>
                            <span style={{ color: on ? t.accent : t.textMuted, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── 선택 작업 줄 ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACING.sm, padding: `0 ${SPACING.xxs}px`, flexWrap: 'wrap' }}>
          {isAdmin ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap', minWidth: 0 }}>
              <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>
                선택 <span style={{ color: t.accent, fontWeight: FONT_WEIGHT.bold }}>{selIds.length}</span>건
              </span>
              <AppButton variant="default" size="sm" disabled={!selIds.length}
                onClick={() => message.info('선택 가격 일괄 수정은 준비 중입니다 — 개별 셀에서 수정해 주세요.')}>
                선택 가격 일괄 수정
              </AppButton>
              <AppButton variant="default" size="sm" disabled={!selIds.length} onClick={bulkStockZero}>
                선택 재고 0으로
              </AppButton>
            </div>
          ) : (
            // 조회 전용 — 선택 · 일괄 수정 · 엑셀 업로드는 숨기고 이유를 한 줄로(유저 플로우 제안 7). 표의 입력 칸도 잠긴다.
            <span data-testid="store-products-readonly" role="note" style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>
              {`${READ_ONLY_REASON} — 가격 · 재고 수정 · 엑셀 업로드는 관리자가 합니다`}
            </span>
          )}
          {isAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap' }}>
              <AppButton variant="default" size="sm" icon={<CloudDownloadOutlined />} loading={downloading}
                disabled={!expansionPicked} disabledReason={excelDisabledReason} onClick={handleDownloadExcel}>
                재고조사 엑셀 다운로드
              </AppButton>
              <AppButton variant="primary" size="sm" icon={<UploadOutlined />}
                disabled={!expansionPicked} disabledReason={excelDisabledReason}
                onClick={() => { setUploadResult(null); setUploadOpen(true); }}>
                엑셀 업로드로 재고 맞추기
              </AppButton>
            </div>
          )}
        </div>

        {/* ── 표 ── */}
        {/* 재조회만 실패하고 보여 줄 목록(이전 조회 결과)이 있으면 표를 지우지 않고 위에
            한 줄로 알린다(2026-10-19 리뷰) — 예전엔 rows 가 있으면 오류가 화면 어디에도
            안 보였다. 현재 페이지가 0건이어도 성공한 조회가 있었으면(전체 >0 · 필터 결과
            0건 포함) 인라인으로 알린다 — 전체 오류 화면은 첫 조회 실패에만 쓴다
            (2026-10-21 리뷰, 딥씨 경계 지적). */}
        {error && !failedEmpty && (
          <AppLoadError inline message={error.message} error={error} onRetry={retry} />
        )}
        <div style={{ ...cardStyle(t), overflow: 'hidden' }}>
          <div style={{ maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: tableCols, alignItems: 'center',
              position: 'sticky', top: 0, zIndex: 10, background: t.bgRaised,
              borderBottom: `1px solid ${t.border}`, fontSize: FONT_SIZE.xs,
              fontWeight: FONT_WEIGHT.semibold, color: t.textSecondary,
            }}>
              <label style={CHECKBOX_CELL} title={isAdmin ? undefined : READ_ONLY_REASON}>
                <input type="checkbox" checked={allChecked} onChange={toggleAll} disabled={!isAdmin}
                  aria-label="이 페이지 전체 선택"
                  style={{ ...CHECKBOX_STYLE, accentColor: t.accent, cursor: isAdmin ? 'pointer' : 'not-allowed' }} />
              </label>
              <div style={{ padding: `${SPACING.sm}px ${SPACING.xs}px`, textAlign: 'center' }}>이미지</div>
              <div style={{ padding: `${SPACING.sm}px` }}>상품명</div>
              <div style={{ padding: `${SPACING.sm}px` }}>판매자코드</div>
              <div style={{ padding: `${SPACING.sm}px`, textAlign: 'right' }}>조회가</div>
              <div style={{ padding: `${SPACING.sm}px`, textAlign: 'right', color: t.accent }}>판매가 (수정)</div>
              <div style={{ padding: `${SPACING.sm}px`, textAlign: 'right', color: t.accent }}>재고 (수정)</div>
              <div style={{ padding: `${SPACING.sm}px`, textAlign: 'center' }}>판매상태</div>
              <div style={{ padding: `${SPACING.sm}px`, textAlign: 'center' }}>가격 반영</div>
              <div style={{ padding: `${SPACING.sm}px`, textAlign: 'center' }}>재고 반영</div>
            </div>

            {rows.map((card) => {
              const priceVal = priceEdits.get(card.id) ?? card.currentPrice ?? 0;
              return (
                <ProductRow
                  key={card.id}
                  card={card}
                  t={t}
                  gridTemplateColumns={tableCols}
                  checked={sel.has(card.id)}
                  readOnly={!isAdmin}
                  priceEdited={priceEdits.has(card.id)}
                  stockEdited={Math.floor(cart.get(card.id)?.value ?? 0) !== 0}
                  priceValue={won(priceVal)}
                  stockValue={String(resolveTargetStock(card))}
                  onCheck={() => toggleRow(card.id)}
                  onPrice={(raw) => setPrice(card.id, raw)}
                  onStock={(raw) => setStock(card.id, raw)}
                />
              );
            })}

            {rows.length === 0 && (
              failedEmpty ? (
                <AppLoadError message={error?.message} error={error} onRetry={retry} />
              ) : (
                <div style={{ padding: `${SPACING['3xl']}px 0`, textAlign: 'center', fontSize: FONT_SIZE.sm, color: t.textMuted }}>
                  {loading
                    ? '상품을 불러오는 중…'
                    : error
                      // 재조회만 실패해 최신 목록을 확인할 수 없다 — "조건에 맞는 상품이 없다"로
                      // 위장하지 않고 위 배너를 보라고 안내한다(2026-10-21 리뷰).
                      ? '목록을 다시 불러오지 못했습니다 — 위 안내에서 다시 시도해 주세요'
                      : '조건에 맞는 상품이 없습니다 — 검색어나 필터를 확인하세요'}
                </div>
              )
            )}
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACING.sm,
            flexWrap: 'wrap',
            padding: `${SPACING.xs}px ${SPACING.md}px`, borderTop: `1px solid ${t.border}`, background: t.bgRaised,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, minWidth: 0, flexWrap: 'wrap' }}>
              <span style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, whiteSpace: 'nowrap' }}>
                {won(total)}건 중 {won(from)}–{won(to)}
              </span>
              <div style={{ width: 1, height: 16, background: t.border }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs, flexWrap: 'wrap' }}>
                {SIZE_OPTIONS.map((n) => (
                  <div key={n} onClick={() => { setSize(n); setPage(1); setJump('1'); }}
                    style={{ ...pageBtnStyle(t, n === size, false), minWidth: 38 }}>
                    {won(n)}
                  </div>
                ))}
                <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, paddingLeft: SPACING.xxs }}>개씩</span>
              </div>
              {heavyWarning && (
                <span style={{
                  fontSize: FONT_SIZE.xs, color: t.warning, background: t.tintWarning,
                  border: `1px solid ${t.lineWarning}`, borderRadius: RADIUS.sm,
                  padding: `${SPACING.xxs}px ${SPACING.xs}px`, whiteSpace: 'nowrap',
                }}>
                  한 번에 많이 불러오면 표가 느려질 수 있어요
                </span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, flex: '0 1 auto', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs, flexWrap: 'wrap' }}>
                <div style={pageBtnStyle(t, false, curPage === 1)} onClick={() => goPage(curPage - 1)}>이전</div>
                {pageNums.map((n, i) => (
                  <React.Fragment key={n}>
                    {i > 0 && n - pageNums[i - 1] > 1 && (
                      <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, padding: `0 ${SPACING.xxs}px` }}>…</span>
                    )}
                    <div style={pageBtnStyle(t, n === curPage, false)} onClick={() => goPage(n)}>{n}</div>
                  </React.Fragment>
                ))}
                <div style={pageBtnStyle(t, false, curPage === pageCount)} onClick={() => goPage(curPage + 1)}>다음</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs, paddingLeft: SPACING.xs, borderLeft: `1px solid ${t.border}` }}>
                <span style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, whiteSpace: 'nowrap' }}>페이지 이동</span>
                <Input size="small" value={jump}
                  onChange={(e) => setJump(e.target.value.replace(/[^0-9]/g, ''))}
                  onPressEnter={() => goPage(parseInt(jump || '1', 10) || 1)}
                  style={{ width: 58, textAlign: 'center' }} />
                <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, whiteSpace: 'nowrap' }}>/ {pageCount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 수정 저장 바 (ADMIN만 — 쓰기 권한) ── */}
      {isAdmin && dirtyCount > 0 && (
        <div style={{
          position: 'fixed', left: compact ? 0 : LAYOUT.sidebarWidth, right: 0, bottom: 0, zIndex: 40,
          background: t.bgSurface, borderTop: `1px solid ${t.border}`, boxShadow: SHADOW.raised,
          padding: compact ? `${SPACING.sm}px ${SPACING.md}px` : `${SPACING.sm}px ${SPACING.xl}px`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: SPACING.sm, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap', minWidth: 0 }}>
            <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>수정 {dirtyCount}건</span>
            <span style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>가격 {priceEdits.size} · 재고 {cartDirtyIds.length}</span>
            {/* 390px 에서는 안내 문구까지 넣으면 바가 두 줄로 넘쳐 표를 가린다 — 좁은 폭에서 숨긴다. */}
            {!compact && (
              <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>저장하면 DB 반영 후 네이버 반영 작업이 시작됩니다</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap', marginLeft: 'auto' }}>
            <AppButton variant="default" onClick={revert} disabled={saving}>되돌리기</AppButton>
            <AppButton variant="primary" size={compact ? 'lg' : 'md'} onClick={save} loading={saving || applying || jobs.naverSyncing}>변경사항 저장 · 반영</AppButton>
          </div>
        </div>
      )}

      {/* ── 엑셀 업로드 모달 ── */}
      <AppModal
        title="엑셀 업로드로 재고 맞추기"
        open={uploadOpen}
        onCancel={() => setUploadOpen(false)}
        width={520}
        footer={[
          <AppButton key="cancel" variant="default" onClick={() => setUploadOpen(false)}>취소</AppButton>,
          <AppButton key="apply" variant="primary" disabled={!uploadResult || uploadResult.matched.length === 0} onClick={applyUpload}>
            재고 수정 대기에 담기
          </AppButton>,
        ]}
      >
        <input ref={fileInputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleFileSelected} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, lineHeight: LINE_HEIGHT.relaxed }}>
            재고조사 엑셀({expansionFilter !== 'ALL' ? expansionFilter : '선택한 확장팩'})을 그대로 올리면 판매자코드 기준으로 재고를 맞춥니다.
            업로드 자체는 DB를 바꾸지 않고, 매칭된 상품을 재고 수정 대기에 담습니다.
          </div>
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: SPACING.xxs,
              height: 120, borderRadius: RADIUS.lg, cursor: 'pointer',
              border: `1px dashed ${uploadResult ? t.lineSuccess : t.lineAccent}`,
              background: uploadResult ? t.tintSuccess : t.bgRaised,
            }}
          >
            <div style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>
              {uploading ? '업로드 중…' : uploadResult ? '다시 선택하려면 클릭' : '엑셀 파일을 선택하세요 (.xlsx)'}
            </div>
            <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>판매자코드 · 실재고 2개 열</div>
          </div>
          {uploadResult && (
            <div style={{ border: `1px solid ${t.border}`, borderRadius: RADIUS.lg, overflow: 'hidden' }}>
              {[
                ['코드 매칭', `${won(uploadResult.matched.length)}건`, t.success],
                ['매칭 실패 (스토어 미등록)', `${won(uploadResult.unmatchedCodes.length)}건`, t.danger],
                ['실제 변경될 재고', `${won(uploadResult.matched.filter((m) => m.uploadedQuantity !== m.currentStock).length)}건`, t.textPrimary],
              ].map(([label, value, color], i, arr) => (
                <div key={label as string} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: `${SPACING.xs}px ${SPACING.md}px`,
                  borderBottom: i < arr.length - 1 ? `1px solid ${t.borderSubtle}` : 'none',
                  background: i === arr.length - 1 ? t.bgRaised : 'transparent', fontSize: FONT_SIZE.xs,
                }}>
                  <span style={{ color: t.textSecondary }}>{label}</span>
                  <span style={{ color: color as string, fontWeight: FONT_WEIGHT.semibold }}>{value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </AppModal>
    </DashboardLayout>
  );
};
